/**
 * HTTP 请求封装工具类
 * 基于 fetch API 实现
 */

class HttpClient {
  constructor(baseURL = '', options = {}) {
    this.baseURL = baseURL
    this.defaultOptions = {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      },
      ...options
    }

    // 请求拦截器
    this.requestInterceptors = []
    // 响应拦截器
    this.responseInterceptors = []
  }

  /**
   * 添加请求拦截器
   * @param {Function} fulfilled - 成功回调
   * @param {Function} rejected - 失败回调
   */
  addRequestInterceptor(fulfilled, rejected) {
    this.requestInterceptors.push({ fulfilled, rejected })
  }

  /**
   * 添加响应拦截器
   * @param {Function} fulfilled - 成功回调
   * @param {Function} rejected - 失败回调
   */
  addResponseInterceptor(fulfilled, rejected) {
    this.responseInterceptors.push({ fulfilled, rejected })
  }

  /**
   * 处理请求拦截器
   * @param {Object} config - 请求配置
   * @returns {Promise<Object>}
   */
  async processRequestInterceptors(config) {
    for (const interceptor of this.requestInterceptors) {
      try {
        if (interceptor.fulfilled) {
          config = await interceptor.fulfilled(config)
        }
      } catch (error) {
        if (interceptor.rejected) {
          throw interceptor.rejected(error)
        }
        throw error
      }
    }
    return config
  }

  /**
   * 处理响应拦截器
   * @param {Response} response - 响应对象
   * @returns {Promise<Response>}
   */
  async processResponseInterceptors(response) {
    let result = response

    for (const interceptor of this.responseInterceptors) {
      try {
        if (interceptor.fulfilled) {
          result = await interceptor.fulfilled(result)
        }
      } catch (error) {
        if (interceptor.rejected) {
          throw interceptor.rejected(error)
        }
        throw error
      }
    }

    return result
  }

  /**
   * 合并请求选项
   * @param {Object} options - 请求选项
   * @returns {Object}
   */
  mergeOptions(options) {
    return {
      ...this.defaultOptions,
      ...options,
      headers: {
        ...this.defaultOptions.headers,
        ...options.headers
      }
    }
  }

  /**
   * 构建完整URL
   * @param {string} url - 请求路径
   * @returns {string}
   */
  buildURL(url) {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return `${this.baseURL}${url}`
  }

  /**
   * 创建超时控制器
   * @param {number} timeout - 超时时间
   * @returns {AbortController}
   */
  createTimeoutController(timeout) {
    const controller = new AbortController()
    setTimeout(() => controller.abort(), timeout)
    return controller
  }

  /**
   * 发送HTTP请求
   * @param {string} url - 请求URL
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  async request(url, options = {}) {
    try {
      // 合并选项
      const mergedOptions = this.mergeOptions(options)

      // 处理请求拦截器
      const config = await this.processRequestInterceptors({
        url: this.buildURL(url),
        ...mergedOptions
      })

      // 创建超时控制器
      const controller = this.createTimeoutController(mergedOptions.timeout)

      // 发送请求
      const response = await fetch(config.url, {
        ...config,
        signal: controller.signal
      })

      // 处理响应拦截器
      const processedResponse = await this.processResponseInterceptors(response)

      // 返回拦截器处理后的结果
      return processedResponse

    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout')
      }
      throw error
    }
  }

  /**
   * GET请求
   * @param {string} url - 请求URL
   * @param {Object} params - 查询参数
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  get(url, params = {}, options = {}) {
    const queryString = new URLSearchParams(params).toString()
    const fullUrl = queryString ? `${url}?${queryString}` : url

    return this.request(fullUrl, {
      method: 'GET',
      ...options
    })
  }

  /**
   * POST请求
   * @param {string} url - 请求URL
   * @param {Object} data - 请求数据
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  post(url, data = {}, options = {}) {
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    })
  }

  /**
   * PUT请求
   * @param {string} url - 请求URL
   * @param {Object} data - 请求数据
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  put(url, data = {}, options = {}) {
    return this.request(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options
    })
  }

  /**
   * DELETE请求
   * @param {string} url - 请求URL
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  delete(url, options = {}) {
    return this.request(url, {
      method: 'DELETE',
      ...options
    })
  }

  /**
   * PATCH请求
   * @param {string} url - 请求URL
   * @param {Object} data - 请求数据
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  patch(url, data = {}, options = {}) {
    return this.request(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options
    })
  }

  /**
   * 上传文件
   * @param {string} url - 请求URL
   * @param {FormData} formData - 表单数据
   * @param {Object} options - 请求选项
   * @returns {Promise}
   */
  upload(url, formData, options = {}) {
    return this.request(url, {
      method: 'POST',
      body: formData,
      headers: {}, // 让浏览器自动设置Content-Type
      ...options
    })
  }
}

export default HttpClient